import { Injectable, computed, signal } from '@angular/core';
import { ImageAsset } from './models';

/**
 * The pair chosen in the library and carried across to the compare view.
 * Kept in a service rather than route params so the choice survives navigation.
 */
@Injectable({ providedIn: 'root' })
export class SelectionService {
  private readonly selected = signal<ImageAsset[]>([]);

  readonly items = this.selected.asReadonly();
  readonly count = computed(() => this.selected().length);
  readonly isComplete = computed(() => this.selected().length === 2);

  /**
   * Older capture first. Falls back to selection order when the two share a
   * capture time, since there is then nothing to order by.
   */
  readonly ordered = computed<[ImageAsset, ImageAsset] | null>(() => {
    const items = this.selected();
    if (items.length !== 2) {
      return null;
    }

    const [first, second] = items;
    return Date.parse(first.capturedAt) <= Date.parse(second.capturedAt)
      ? [first, second]
      : [second, first];
  });

  /** The older of the pair, or null until two are chosen. */
  readonly before = computed(() => this.ordered()?.[0] ?? null);

  /** The newer of the pair, or null until two are chosen. */
  readonly after = computed(() => this.ordered()?.[1] ?? null);

  /** True when the capture times differ, so before/after is unambiguous. */
  readonly orderIsUnambiguous = computed(() => {
    const items = this.selected();
    return items.length === 2 && items[0].capturedAt !== items[1].capturedAt;
  });

  isSelected(id: string): boolean {
    return this.selected().some((image) => image.id === id);
  }

  /** Selecting a third image replaces the oldest choice, so the pair is never stuck. */
  toggle(image: ImageAsset): void {
    this.selected.update((current) => {
      if (current.some((i) => i.id === image.id)) {
        return current.filter((i) => i.id !== image.id);
      }
      return current.length < 2 ? [...current, image] : [current[1], image];
    });
  }

  set(before: ImageAsset, after: ImageAsset): void {
    this.selected.set([before, after]);
  }

  clear(): void {
    this.selected.set([]);
  }

  /**
   * Swaps in a fresh copy of an already-selected image, keeping its position in the
   * pair. Used after editing metadata — an extent, say — so the selection does not go
   * on describing the image as it was before the edit.
   */
  replace(image: ImageAsset): void {
    this.selected.update((current) => current.map((i) => (i.id === image.id ? image : i)));
  }

  /** Drops any selected image that no longer exists in the collection. */
  reconcile(available: ImageAsset[]): void {
    const ids = new Set(available.map((i) => i.id));
    this.selected.update((current) => current.filter((i) => ids.has(i.id)));
  }
}
